// src/Screens/Workflow/AuthorisedUsersWorkflowModal.jsx
import React, { useState, useEffect } from "react";
import { TenantID, DELETE, GET } from "./../dataSaver";
import { Dialog, Classes, Icon, Intent } from "@blueprintjs/core";
import style from "./index.module.scss";
import ContractHeader from "../../Components/ContractHeader";
import redAlert from "../../images/redAlert.png";
import yellowAlert from "../../images/yellowAlert.png";
import { format } from "date-fns";
import fileIcon from "../../images/redAlert.png";
import yellowWarning from "../../images/yellowWarning.png";
import CompletedIcon from "../../images/checkImage.png";
import Loader from "../../images/loading.gif";
import greenCheck from "../../images/GreenTick.png";
import Access from "./Access";

const AuthorisedUsersWorkflowModal = ({
  onClose,
  contractTitle,
  contractNumber,
  contractType,
  lastUpdated,
  onModifyWorkflow,
  contractId,
}) => {
  const [apiContractData, setApiContractData] = useState(null);
  const [workFlowData, setWorkFlowData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showAccess, setShowAccess] = useState(false);

  useEffect(() => {
    fetchContractData();
    fetchWorkFlowData();
  }, []);

  console.log("fetchContractData", contractId, workFlowData);

  // Check if tenant ID exists to hide Financial Reviewer section
  const shouldHideFinancialReviewer = () => {
    const targetTenantId = "64246d491b70b07241d37aa1";
    console.log("Checking tenant ID - workFlowData:", workFlowData);
    console.log("workFlowData.tenant:", workFlowData?.tenant);
    console.log("targetTenantId:", targetTenantId);
    
    // Check multiple possible locations for tenant ID
    const tenantMatch = workFlowData?.tenant === targetTenantId || 
                       workFlowData?.tenantId === targetTenantId ||
                       workFlowData?.tenant?.id === targetTenantId;
    
    console.log("Should hide Financial Reviewer:", tenantMatch);
    return tenantMatch;
  };

  const fetchContractData = async () => {
    if (!contractId) {
      console.log("No contractId provided, skipping API call");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const apiUrl = `contract-managment-service/contracts/${contractId}`;
      console.log("Fetching contract data with API URL:", apiUrl);

      const response = await GET(apiUrl);
      console.log("Contract API response:", response);

      if (response.data) {
        setApiContractData(response.data);
        console.log("Contract data fetched successfully:", response.data);
      }
    } catch (error) {
      console.error("Error fetching contract data:", error);
      setError("Failed to fetch contract data");
    } finally {
      setLoading(false);
    }
  };

  const fetchWorkFlowData = async () => {
    setLoading(true);
    try {
      const apiUrl = `timesheet-management-service/contractWorkflow/${contractId}`;
      const response = await GET(apiUrl);

      if (response.data) {
        setWorkFlowData(response.data);
        console.log("Contract data fetched successfully:", response.data);
      }
    } catch (error) {
      console.error("Error fetching contract data:", error);
      setError("Failed to fetch contract data");
    } finally {
      setLoading(false);
    }
  };

  // Get workflow section status (same logic as ViewSourceRecord)
  const getWorkflowSectionStatus = (sectionTitle, workflowData) => {
    if (!workflowData) {
      return "inactive";
    }

    switch (sectionTitle) {
      case "Activity Logging":
        if (workflowData.timesheetWorkflows && workflowData.timesheetWorkflows.length > 0) {
          const hasIncompleteActivityLogging = workflowData.timesheetWorkflows.some(workflow => {
            const activityLogDetail = workflow.activityLoggerDetail;
            return !activityLogDetail || activityLogDetail.completionStatus !== "COMPLETED";
          });

          const hasPartialActivityLogging = workflowData.timesheetWorkflows.some(workflow => {
            const activityLogDetail = workflow.activityLoggerDetail;
            return activityLogDetail && activityLogDetail.completionStatus === "PARTIAL";
          });

          if (hasIncompleteActivityLogging) {
            return hasPartialActivityLogging ? "partial" : "not_completed";
          }
          return "completed";
        }
        return "inactive";

      case "Timesheet Creation & Submission":
        if (workflowData.timesheetWorkflows && workflowData.timesheetWorkflows.length > 0) {
          const hasSubmissionUsers = workflowData.timesheetWorkflows.some(workflow =>
            workflow.timesheetSubmissionUser !== null
          );

          if (!hasSubmissionUsers) {
            return "not_completed";
          }

          const hasIncompleteSubmission = workflowData.timesheetWorkflows.some(workflow => {
            const submissionUser = workflow.timesheetSubmissionUser;
            return submissionUser && submissionUser.completionStatus !== "COMPLETED";
          });

          const hasPartialSubmission = workflowData.timesheetWorkflows.some(workflow => {
            const submissionUser = workflow.timesheetSubmissionUser;
            return submissionUser && submissionUser.completionStatus === "PARTIAL";
          });

          if (hasIncompleteSubmission) {
            return hasPartialSubmission ? "partial" : "not_completed";
          }
          return "completed";
        }
        return "inactive";

      case "Timesheet Review & Approval":
        if (workflowData.timesheetWorkflows && workflowData.timesheetWorkflows.length > 0) {
          const hasWorkflowConfig = workflowData.timesheetWorkflows.some(workflow =>
            workflow.timesheetWorkflowConfiguration !== null
          );

          if (!hasWorkflowConfig) {
            return "not_completed";
          }

          const hasIncompleteWorkflow = workflowData.timesheetWorkflows.some(workflow => {
            const config = workflow.timesheetWorkflowConfiguration;
            if (!config) return true;

            if (config.completionStatus === "NOT_COMPLETED") return true;
            if (!config.hasTimesheetApproval) return true;
            if (!config.authorizedUserIds || config.authorizedUserIds.length === 0) return true;

            return false;
          });

          const hasPartialWorkflow = workflowData.timesheetWorkflows.some(workflow => {
            const config = workflow.timesheetWorkflowConfiguration;
            return config && config.completionStatus === "PARTIAL";
          });

          if (hasIncompleteWorkflow) {
            return hasPartialWorkflow ? "partial" : "not_completed";
          }
          return "completed";
        }
        return "inactive";

      case "Invoice Review & Approval":
        if (workflowData.invoiceWorkflows && workflowData.invoiceWorkflows.length > 0) {
          const hasIncompleteInvoice = workflowData.invoiceWorkflows.some(invoiceWorkflow => {
            if (invoiceWorkflow.completionStatus === "NOT_COMPLETED") return true;

            return false;
          });

          const hasPartialInvoice = workflowData.invoiceWorkflows.some(invoiceWorkflow => {
            return invoiceWorkflow.completionStatus === "PARTIAL";
          });

          if (hasIncompleteInvoice) {
            return hasPartialInvoice ? "partial" : "not_completed";
          }
          return "completed";
        }
        return "inactive";

      default:
        return "inactive";
    }
  };
console.log(apiContractData?.contract,'hjbkjbkjnk')
  const getAlertsAndIcons = () => {
    const alerts = [];
    const alertIcons = [];
    

    if (apiContractData?.contract) {
      const contract = apiContractData.contract;

      let hasDocuments = false;
      let documentCount = 0;

      if (contract.contractDetail?.contractFiles && contract.contractDetail.contractFiles.length > 0) {
        const validDocuments = contract.contractDetail.contractFiles.filter(file =>
          file && file.fileURL && file.fileURL.trim() !== ''
        );

        if (validDocuments.length > 0) {
          hasDocuments = true;
          documentCount = validDocuments.length;
        }
      }

      const contractSections = [
        {
          title: "Attached Contract Document(s)",
          subtitle: hasDocuments ? `${documentCount} Documents` : "No Documents",
          status: hasDocuments ? "completed" : "not_completed"
        },
        {
          title: "Contract Identification & Term Limit",
          status: (() => {
            if (contract.contractDetail) {
              return "completed";
            }

            if (!contract.contractDetail) {
              return "inactive";
            }

            return "inactive";
          })()
        },
        {
          title: "Contracted Services Provider(s)",
          status: (() => {
            if (contract.contractServiceProviders?.length > 0) {
              const hasIncompleteProviders = contract.contractServiceProviders.some(provider => {
                const hasBasicInfo = provider.name?.firstName &&
                  provider.name?.lastName &&
                  provider.contractedServiceProviderType?.id;

                return !hasBasicInfo;
              });

              if (hasIncompleteProviders) {
                return "not_completed";
              } else {
                return "completed";
              }
            } else {
              return "completed";
            }
          })()
        },
        {
          title: "Contractor Business Entity",
          status: (() => {
            if (!contract.contractorBusinessEntity || contract.contractorBusinessEntity.id === null) {
              return "inactive";
            }

            if (contract.contractorBusinessEntity.completionStatus === "NOT_COMPLETED") {
              return "not_completed";
            }
            if (contract.contractorBusinessEntity.completionStatus === "PARTIAL") {
              return "partial";
            }
            if (contract.contractorBusinessEntity.completionStatus === "COMPLETED") {
              return "completed";
            }

            return "inactive";
          })()
        },
        {
          title: "Contracted Services Specification",
          status: (() => {
            if (!contract.contractedServices || contract.contractedServices.length === 0) {
              return "inactive";
            }

            const hasNotCompletedServices = contract.contractedServices.some(service =>
              service.completionStatus === "NOT_COMPLETED"
            );
            const hasPartialServices = contract.contractedServices.some(service =>
              service.completionStatus === "PARTIAL"
            );
            const hasCompletedServices = contract.contractedServices.some(service =>
              service.completionStatus === "COMPLETED"
            );

            if (hasNotCompletedServices) {
              return "not_completed";
            }
            if (hasPartialServices) {
              return "partial";
            }
            if (hasCompletedServices) {
              return "completed";
            }

            return "inactive";
          })()
        },
        {
          title: "Timesheet Submission Terms",
          status: (() => {
            const timesheetTermsArray = Array.isArray(contract.timesheetSubmissionTerms) ?
              contract.timesheetSubmissionTerms :
              (contract.timesheetSubmissionTerms ? [contract.timesheetSubmissionTerms] : []);

            if (timesheetTermsArray.length === 0) {
              return "inactive";
            }

            const hasNotCompletedTerms = timesheetTermsArray.some(terms =>
              terms.completionStatus === "NOT_COMPLETED"
            );
            const hasPartialTerms = timesheetTermsArray.some(terms =>
              terms.completionStatus === "PARTIAL"
            );
            const hasCompletedTerms = timesheetTermsArray.some(terms =>
              terms.completionStatus === "COMPLETED"
            );

            if (hasNotCompletedTerms) {
              return "not_completed";
            }
            if (hasPartialTerms) {
              return "partial";
            }
            if (hasCompletedTerms) {
              return "completed";
            }

            return "inactive";
          })()
        },
        {
          title: "Payment & Compensation",
          status: (() => {
            if (!contract.paymentAndCompensation) {
              return "inactive";
            }
            if (contract.paymentAndCompensation.completionStatus === "NOT_COMPLETED") {
              return "not_completed";
            }
            if (contract.paymentAndCompensation.completionStatus === "PARTIAL") {
              return "partial";
            }
            if (contract.paymentAndCompensation.completionStatus === "COMPLETED") {
              return "completed";
            }

            return "inactive";
          })()
        }
      ];

      // Workflow validation
      const workflowSections = [
        {
          title: "Activity Logging",
          status: getWorkflowSectionStatus("Activity Logging", workFlowData)
        },
        {
          title: "Timesheet Creation & Submission",
          status: getWorkflowSectionStatus("Timesheet Creation & Submission", workFlowData)
        },
        {
          title: "Timesheet Review & Approval",
          status: getWorkflowSectionStatus("Timesheet Review & Approval", workFlowData)
        }
      ];

      // Only include Invoice Review & Approval if tenant ID doesn't match
      if (!shouldHideFinancialReviewer()) {
        workflowSections.push({
          title: "Invoice Review & Approval",
          status: getWorkflowSectionStatus("Invoice Review & Approval", workFlowData)
        });
      }

      console.log('Workflow Data:', workFlowData);
      console.log('Workflow Sections Status:', workflowSections.map(section => ({ title: section.title, status: section.status })));

      const sectionsWithWarnings = contractSections.filter(section => section.status === 'not_completed');
      const sectionsWithPartials = contractSections.filter(section => section.status === 'partial');
      const allContractSectionsCompleted = contractSections.every(section => section.status === 'completed');

      const incompleteWorkflows = workflowSections.filter(section =>
        section.status === 'inactive' || section.status === 'not_completed'
      );
      const partialWorkflows = workflowSections.filter(section => section.status === 'partial');
      const allWorkflowsCompleted = workflowSections.every(section => section.status === 'completed');

      if (sectionsWithWarnings.length > 0) {
        alerts.push("Contract Data Required");
      } else if (sectionsWithPartials.length > 0) {
        alerts.push("Contract Data Partially Completed");
      } else {
        alerts.push("Contract Data Completed");
      }

      if (incompleteWorkflows.length > 0) {
        alerts.push("Workflow Configuration Incomplete");
      } else if (partialWorkflows.length > 0) {
        alerts.push("Workflow Configuration Partially Completed");
      } else {
        alerts.push("Workflow Configuration Completed");
      }

      if (sectionsWithWarnings.length > 0) {
        alertIcons.push(fileIcon);
      } else if (sectionsWithPartials.length > 0) {
        alertIcons.push(yellowWarning);
      } else if (allContractSectionsCompleted) {
        alertIcons.push(CompletedIcon);
      } else {
        alertIcons.push(fileIcon);
      }

      if (incompleteWorkflows.length > 0) {
        alertIcons.push(fileIcon);
      } else if (partialWorkflows.length > 0) {
        alertIcons.push(yellowWarning);
      } else if (allWorkflowsCompleted) {
        alertIcons.push(CompletedIcon);
      } else {
        alertIcons.push(fileIcon);
      }

    } else {
      alerts.push("Contract Data Not Available");
      alertIcons.push(fileIcon);
      alerts.push("Workflow Data Not Available");
      alertIcons.push(fileIcon);
    }

    return { alerts, alertIcons };
  };

  const { alerts, alertIcons } = getAlertsAndIcons();

  // Handler to close this modal and handle modify workflow
  const handleModifyWorkflow = () => {
    // Open the Access modal instead of delegating up
    setShowAccess(true);
  };

  return (
    <>
      <Dialog
        isOpen={true}
        onClose={onClose}
        className={`${style.dialogStyle} ${style.dialogPaddingBottom}`}
      >
        <div
          className={`${Classes.DIALOG_BODY} ${style.extensionDialogBackground}`}
        >
          {loading ? (
            <div
              className={`${style.verticalAlignCenter} ${style.justifyCenter}`}
            >
              <img src={Loader} alt="Loading" width={250} />
            </div>
          ) : (
            <>
              <div
                className={style.spaceBetween}
                style={{ alignItems: "center", display: "flex" }}
              >
                <div className={style.extensionStyle}>
                  Authorized Users Designated for Workflow steps
                </div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    height: "auto",
                  }}
                >
                  {/* Commented out Modify Workflow button as requested */}
                  {/*
                  <div
                    className={`${style.primaryBackgroundStyle} ${style.whiteTextStyle}`}
                    onClick={handleModifyWorkflow}
                  >
                    Modify Workflow
                  </div>
                  */}
                  <Icon
                    icon="cross"
                    size={20}
                    intent={Intent.DANGER}
                    onClick={onClose}
                    style={{
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      height: "32px",
                    }}
                  />
                </div>
              </div>
              <div className={style.extensionBorder}></div>

              {apiContractData?.contract?.contractName?.contractName && (
                <div className={style.marginTop20}>
                  <ContractHeader
                    contractTitle={
                      apiContractData?.contract?.contractName?.contractName ||
                      "Contract Name"
                    }
                    isDraft={
                      apiContractData?.contract?.contractStatus === "DRAFT"
                    }
                    contractSubtitle={
                      apiContractData?.contract?.contractType
                        ? apiContractData.contract.contractType === "MULTIPLE"
                          ? "Multi Provider Contract"
                          : apiContractData.contract.contractType === "BATCH"
                          ? "Batch Provider Contract"
                          : "Solo Provider Contract"
                        : "Solo Provider Contract"
                    }
                    contractId={
                      apiContractData?.contract?.contractDetail?.contractId
                        ?.id || "Contract ID"
                    }
                    alerts={alerts}
                    alertIcons={alertIcons}
                    alertIcon={fileIcon}
                    lastUpdated={
                      apiContractData?.contract?.lastModifiedDate
                        ? format(
                            new Date(apiContractData.contract.lastModifiedDate),
                            "MMM dd, yyyy"
                          )
                        : "N/A"
                    }
                  />
                </div>
              )}

              <div className={`${style.marginTop20}`}>
                <div className={style.contractorsLabelStyle}>
                  Activity Logger(s)
                </div>
                <div className={style.contractorsList}>
                  {workFlowData?.timesheetWorkflows?.length > 0 ? (
                    workFlowData.timesheetWorkflows.some(
                      (workflow) =>
                        workflow?.activityLoggerDetail?.activityLogUsers
                          ?.length > 0
                    ) ? (
                      workFlowData.timesheetWorkflows.map((workflow) =>
                        workflow?.activityLoggerDetail?.activityLogUsers?.map(
                          (user) => {
                            let roleLabel = "";
                            if (
                              user.rolePerformerType ===
                              "CONTRACT_SERVICE_PROVIDER"
                            ) {
                              roleLabel = "Service Provider";
                            } else if (user.rolePerformerType === "SURROGATE") {
                              roleLabel = "Surrogate";
                            } else if (user.rolePerformerType === "PROXY") {
                              roleLabel = "Proxy";
                            }  else if (user.rolePerformerType === "CONTRACT_SERVICE_PROVIDER_PROXY") {
                              roleLabel = "Service Provider Proxy";
                            }

                            return (
                              <div
                                key={user?.id}
                                className={style.contractorItem}
                              >
                                <b>{roleLabel}</b> :{" "}
                                {
                                  workflow?.timesheetDefinition?.timesheetLabel
                                    ?.label
                                }{" "}
                                {"-"} {user?.name?.name || "Unknown User"}
                                {user?.sites ? ` - ${user.sites}` : ""}
                              </div>
                            );
                          }
                        )
                      )
                    ) : (
                      <div className={style.contractorItem}>No user found</div>
                    )
                  ) : (
                    <div className={style.contractorItem}>No user found</div>
                  )}
                </div>
              </div>

              <div className={`${style.marginTop20}`}>
                <div
                  className={style.contractorsLabelStyle}
                  style={{ color: "#222", fontWeight: 700 }}
                >
                  Timesheet Submitter(s)
                </div>

                <div className={style.contractorsList}>
                  {workFlowData?.timesheetWorkflows?.map(
                    (workflow, wfIndex) => {
                      const submitterConfig = workflow?.timesheetSubmissionUser;
                      const users =
                        submitterConfig?.timesheetSubmissionUsers || [];

                      // If no users -> show Not Assigned alert
                      if (!users.length) {
                        return (
                          <div
                            key={`no-user-${wfIndex}`}
                            className={style.contractorItem}
                            style={{
                              color: "#ff3b30",
                              fontWeight: 500,
                              display: "flex",
                              alignItems: "center",
                              gap: 6,
                            }}
                          >
                            <img
                              src={redAlert}
                              alt="Alert"
                              style={{ width: "18px", height: "18px" }}
                            />
                            <span>
                              Not Assigned yet.{" "}
                            </span>
                          </div>
                        );
                      }

                      // If users exist -> show them
                      return users.map((user, idx) => {
                        let roleLabel = "";
                        if (
                          user.rolePerformerType ===
                          "CONTRACT_SERVICE_PROVIDER"
                        ) {
                          roleLabel = "Service Provider";
                        } else if (user.rolePerformerType === "SURROGATE") {
                          roleLabel = "Surrogate";
                        } else if (user.rolePerformerType === "PROXY") {
                          roleLabel = "Proxy";
                        }  else if (user.rolePerformerType === "CONTRACT_SERVICE_PROVIDER_PROXY") {
                          roleLabel = "Service Provider Proxy";
                        }

                        return (
                          <div
                            key={`${wfIndex}-${idx}`}
                            className={style.contractorItem}
                          >
                            <div>
                              <b>{roleLabel}</b> :{" "}
                              {
                                workflow?.timesheetDefinition?.timesheetLabel
                                  ?.label
                              }{" "}
                              {"-"} {user?.name?.name ?? "N/A"}
                              {user?.sites && user.sites.length > 0 && (
                                <span> - {user.sites.join(", ")}</span>
                              )}
                            </div>
                          </div>
                        );
                      });
                    }
                  )}
                </div>
              </div>

              <div className={style.marginTop20}>
                <div className={style.contractorsLabelStyle}>
                  Timesheet Reviewer(s) / Approver(s)
                </div>

                <div className={style.contractorsList}>
                  {workFlowData?.timesheetWorkflows?.map(
                    (workflow, wfIndex) => {
                      const controlLevels =
                        workflow?.timesheetWorkflowConfiguration
                          ?.controlLevels || {};

                      return Object.entries(controlLevels).map(
                        ([levelKey, levelData], levelIndex) => (
                          <div
                            key={`${wfIndex}-${levelIndex}`}
                            className={style.contractorItem}
                          >
                            <div style={{ fontWeight: 600, marginBottom: 8 }}>
                              Control Level {levelIndex + 1}
                            </div>
                            {levelData?.workFlowUser?.rolePerformerType ===
                              "TIMESHEET_REVIEWER_APPROVER" && (
                              <div>
                                <b>Reviewer / Approver</b> :{" "}
                                {
                                  workflow?.timesheetDefinition?.timesheetLabel
                                    ?.label
                                }{" "}
                                {"-"}{" "}
                                {levelData?.workFlowUser?.name?.name ?? "N/A"}
                                {levelData?.sites && (
                                  <span> - {levelData.sites.join(", ")}</span>
                                )}
                              </div>
                            )}
                            {levelData?.proxyUsers?.map((proxy, proxyIdx) => (
                              <div key={proxyIdx}>
                                <b>Proxy</b> :{" "}
                                {
                                  workflow?.timesheetDefinition?.timesheetLabel
                                    ?.label
                                }{" "}
                                {"-"} {proxy?.name?.name ?? "N/A"}
                                {proxy?.sites && (
                                  <span> - {proxy.sites.join(", ")}</span>
                                )}
                              </div>
                            ))}
                          </div>
                        )
                      );
                    }
                  )}
                </div>
              </div>

              {/* Conditionally render Financial Reviewer section */}
              {(() => {
                const hideFinancial = shouldHideFinancialReviewer();
                console.log("Rendering Financial section - should hide:", hideFinancial);
                return !hideFinancial;
              })() && (
                <div className={`${style.marginTop20}`}>
                  <div className={style.contractorsLabelStyle}>
                    Financial Reviewer(s) / Approver(s)
                  </div>
                  {workFlowData?.invoiceWorkflows?.map((workflow, index) => (
                    <div key={index}>
                      {workflow.controlLevels &&
                        Object.entries(workflow.controlLevels).map(
                          ([level, controlLevel]) => (
                            <div key={level}>
                              <div className={style.contractorsList}>
                                <div style={{ fontWeight: 400, marginBottom: 8 }}>
                                  Control Level {level}
                                </div>
                                {controlLevel.workflowUserDetails
                                  ?.filter(
                                    (user) =>
                                      user.rolePerformerType ===
                                      "INVOICE_REVIEWER_APPROVER"
                                  )
                                  .map((user, userIndex, array) => (
                                    <div
                                      key={user.id}
                                      className={`${style.contractorItem} ${
                                        userIndex === array.length - 1
                                          ? style.lastContractorItem
                                          : ""
                                      }`}
                                    >
                                      Reviewer / Approver:{" "}
                                      {user.name?.name ||
                                        `${user.name?.firstName} ${user.name?.lastName}`}
                                    </div>
                                  ))}
                              </div>
                            </div>
                          )
                        )}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </Dialog>

      {/* Access Privileges Modal */}
      {showAccess && <Access onClose={() => setShowAccess(false)} />}
    </>
  );
};

export default AuthorisedUsersWorkflowModal;